import type { PanelModule } from '../types/sdk';

export type PanelLoader = () => Promise<PanelModule>;

/** Resolves the URL of the panel chunk as a sibling of the loader script, keeping its query string (cache busters). */
export function resolvePanelUrl(scriptSrc: string | null | undefined, override?: string): string {
  if (override) return override;
  if (!scriptSrc) return 'assistant-panel.js';
  try {
    const u = new URL(scriptSrc, typeof location !== 'undefined' ? location.href : undefined);
    const file = u.pathname.split('/').pop() ?? '';
    const panelFile = /\.min\.js$/.test(file) ? 'assistant-panel.min.js' : 'assistant-panel.js';
    u.pathname = u.pathname.replace(/[^/]*$/, panelFile);
    return u.toString();
  } catch {
    return 'assistant-panel.js';
  }
}

/** URL of the de/fr/es locale pack: `assistant-locales.js` next to the panel chunk (same query string). */
export function resolveLocalesUrl(panelUrl: string): string {
  try {
    const u = new URL(panelUrl, typeof location !== 'undefined' ? location.href : undefined);
    u.pathname = u.pathname.replace(/[^/]*$/, 'assistant-locales.js');
    return u.toString();
  } catch {
    return 'assistant-locales.js';
  }
}

let localesPending: Promise<void> | null = null;

/**
 * Injects the locale pack once (it sets window.__AICommerceLocales). Never rejects: without it de/fr/es visitors get
 * the English panel.
 */
export function loadLocalePack(url: string): Promise<void> {
  if (window.__AICommerceLocales) return Promise.resolve();
  if (localesPending) return localesPending;
  localesPending = new Promise<void>((resolve) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      localesPending = null;
      script.remove();
      resolve();
    };
    document.head.appendChild(script);
  });
  return localesPending;
}

let pending: Promise<PanelModule> | null = null;

/**
 * Injects the panel IIFE once; it registers itself on window.__AICommercePanel. `localesUrl` (evaluated at load time)
 * names the de/fr/es locale pack to fetch in parallel; the panel resolves once both are there.
 */
export function scriptPanelLoader(url: string, localesUrl?: () => string | null): PanelLoader {
  return () => {
    const pack = localesUrl?.();
    const locales = pack ? loadLocalePack(pack) : Promise.resolve();
    if (window.__AICommercePanel) return locales.then(() => window.__AICommercePanel!);
    if (pending) return pending;
    pending = new Promise<PanelModule>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.onload = () => {
        const mod = window.__AICommercePanel;
        if (mod) void locales.then(() => resolve(mod));
        else reject(new Error('AICommerce panel chunk did not register'));
      };
      script.onerror = () => {
        pending = null;
        script.remove();
        reject(new Error(`AICommerce panel chunk failed to load: ${url}`));
      };
      document.head.appendChild(script);
    });
    return pending;
  };
}
